import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, Users, Swords, Coins, CreditCard, Download, Activity } from "lucide-react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";

const RANGES = [7, 30, 90];

function eur(n) {
  return "€" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const Stat = ({ id, icon: Icon, label, value, sub, accent = "text-signal" }) => (
  <div data-testid={`kpi-stat-${id}`} className="bg-panel border border-white/10 rounded-sm p-4 min-w-0">
    <div className="flex items-center gap-2 text-white/50 text-xs font-mono uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`font-heading font-black text-2xl mt-2 tabular-nums ${accent}`}>{value}</div>
    {sub && <div className="text-xs text-white/40 mt-1 font-mono">{sub}</div>}
  </div>
);

const ChartCard = ({ id, title, children }) => (
  <div data-testid={`kpi-chart-${id}`} className="bg-panel border border-white/10 rounded-sm p-4 min-w-0">
    <div className="text-xs font-mono uppercase tracking-wider text-white/50 mb-3">{title}</div>
    <div className="h-44">{children}</div>
  </div>
);

const tooltipStyle = { background: "#0f1115", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 };
const axis = { stroke: "rgba(255,255,255,0.3)", fontSize: 10 };
const shortDate = (d) => d.slice(5);

export default function AdminKpi() {
  const { user } = useAuth();
  const { t } = useLang();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    setData(null);
    api.get(`/admin/kpi?days=${days}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.status || "err"));
  }, [days, user]);

  if (user && user.role !== "admin") return <Navigate to="/lobby" replace />;

  const exportCsv = () => {
    if (!data) return;
    const cols = Object.keys(data.series[0]);
    const csv = [cols.join(","), ...data.series.map((r) => cols.map((c) => r[c]).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `snetch-kpi-${days}d.csv`;
    a.click();
  };

  const T = data?.totals;
  const W = data?.window;
  const E = data?.engagement;
  const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  const ret = (k) => E?.retention?.[k];

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <div data-testid="admin-kpi-page" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-signal">
              <BarChart3 className="w-5 h-5" />
              <span className="font-mono text-xs uppercase tracking-widest">{t("kpi_tag")}</span>
            </div>
            <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">{t("kpi_title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-white/10 rounded-sm overflow-hidden">
              {RANGES.map((r) => (
                <button key={r} data-testid={`kpi-range-${r}`} onClick={() => setDays(r)}
                  className={`px-3 py-1.5 text-xs font-mono ${days === r ? "bg-signal text-black" : "text-white/60 hover:bg-white/5"}`}>
                  {r}d
                </button>
              ))}
            </div>
            <button data-testid="kpi-export-csv" onClick={exportCsv} disabled={!data}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono border border-white/10 rounded-sm text-white/70 hover:bg-white/5 disabled:opacity-40">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>

        {error && <div data-testid="kpi-error" className="text-loss font-mono text-sm">{t("kpi_error")} ({error})</div>}
        {!data && !error && <div className="font-mono text-white/40 text-sm animate-pulse">loading…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Stat id="users" icon={Users} label={t("kpi_users")} value={T.users} sub={`${t("kpi_verified")}: ${T.verified_users} · ${t("kpi_active7")}: ${T.active_users_7d}`} />
              <Stat id="matches" icon={Swords} label={t("kpi_matches")} value={T.matches} sub={`Sit & Go: ${T.sitgo_matches} · ${t("kpi_finished")}: ${T.matches_finished}`} accent="text-white" />
              <Stat id="rake" icon={Coins} label={t("kpi_rake")} value={eur(T.rake)} sub={`${days}d: ${eur(W.rake)}`} accent="text-profit" />
              <Stat id="deposits" icon={CreditCard} label={t("kpi_deposits")} value={eur(T.deposits_paid)} sub={`${t("kpi_credited")}: ${eur(T.deposits_credited)} · ${days}d: ${eur(W.deposits_paid)}`} accent="text-white" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <Stat id="window-reg" icon={Users} label={`${t("kpi_registrations")} ${days}d`} value={W.registrations} accent="text-white" />
              <Stat id="window-matches" icon={Swords} label={`${t("kpi_matches")} ${days}d`} value={W.matches} sub={`Sit & Go: ${W.sitgo}`} accent="text-white" />
              <Stat id="payouts" icon={Coins} label={t("kpi_payouts")} value={eur(T.payouts)} sub={`${days}d: ${eur(W.payouts)}`} accent="text-white" />
              <Stat id="liabilities" icon={CreditCard} label={t("kpi_liabilities")} value={eur(T.wallet_liabilities)} sub={`${t("kpi_withdrawable")}: ${eur(T.withdrawable_liabilities)}`} accent="text-loss" />
            </div>

            <div className="flex items-center gap-2 text-white/50 text-xs font-mono uppercase tracking-wider mb-3"><Activity className="w-3.5 h-3.5" /> {t("kpi_engagement")}</div>
            <div data-testid="kpi-engagement" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
              <Stat id="dau" icon={Activity} label={t("kpi_dau")} value={E.avg_dau_7d} sub={t("kpi_dau_sub")} accent="text-white" />
              <Stat id="mau" icon={Users} label={t("kpi_mau")} value={E.mau} sub={`${t("kpi_stickiness")}: ${pct(E.stickiness)}`} accent="text-white" />
              <Stat id="ret-d1" icon={Activity} label="Retention D1" value={pct(ret("d1")?.rate)} sub={`${ret("d1")?.retained}/${ret("d1")?.cohort} ${t("kpi_cohort")}`} accent="text-profit" />
              <Stat id="ret-d7" icon={Activity} label="Retention D7" value={pct(ret("d7")?.rate)} sub={`${ret("d7")?.retained}/${ret("d7")?.cohort} ${t("kpi_cohort")}`} accent="text-profit" />
              <Stat id="ret-d30" icon={Activity} label="Retention D30" value={pct(ret("d30")?.rate)} sub={`${ret("d30")?.retained}/${ret("d30")?.cohort} ${t("kpi_cohort")}`} accent="text-profit" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ChartCard id="dau" title={t("kpi_dau_chart")}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series}>
                    <XAxis dataKey="date" tickFormatter={shortDate} {...axis} />
                    <YAxis allowDecimals={false} width={28} {...axis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area dataKey="dau" stroke="#EAB308" fill="#EAB308" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard id="registrations" title={t("kpi_registrations")}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series}>
                    <XAxis dataKey="date" tickFormatter={shortDate} {...axis} />
                    <YAxis allowDecimals={false} width={28} {...axis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="registrations" fill="#EAB308" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard id="matches" title={t("kpi_matches")}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series}>
                    <XAxis dataKey="date" tickFormatter={shortDate} {...axis} />
                    <YAxis allowDecimals={false} width={28} {...axis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="matches" fill="rgba(255,255,255,0.5)" />
                    <Bar dataKey="sitgo" fill="#EAB308" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard id="rake" title={`${t("kpi_rake")} (€)`}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series}>
                    <XAxis dataKey="date" tickFormatter={shortDate} {...axis} />
                    <YAxis width={36} {...axis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area dataKey="rake" stroke="#22C55E" fill="#22C55E" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard id="deposits" title={`${t("kpi_deposits")} (€)`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series}>
                    <XAxis dataKey="date" tickFormatter={shortDate} {...axis} />
                    <YAxis width={36} {...axis} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="deposits_paid" fill="#EAB308" />
                    <Bar dataKey="payouts" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <div className="text-[11px] font-mono text-white/30 mt-4">{t("kpi_generated")} {new Date(data.generated_at).toLocaleString()}</div>
          </>
        )}
      </div>
    </div>
  );
}
